import { useMemo } from 'react';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@/_components';
import { TableHeading } from '@/_containers';
import { noop } from '@/_shared';

import { UploadFileRow } from './upload-file-row';

type FilesUploadModalProps = {
  files: File[];
  isPending: boolean;
  modelOpen: [boolean, (open: boolean) => void];
  onUpload: () => Promise<void>;
  onCancel: () => void;
  onRemove?: (fileName: string) => void;
  disabled?: boolean;
  onAddMore?: () => void;
};

export function FilesUploadModal({
  files,
  isPending,
  modelOpen: [isOpen],
  onUpload,
  onCancel,
  onRemove,
  disabled,
  onAddMore,
}: FilesUploadModalProps) {
  const filesUrls = useMemo(
    () =>
      files.reduce((urls: Record<string, string>, f) => {
        if (f.type.startsWith('image/')) {
          urls[f.name] = URL.createObjectURL(f);
        }
        return urls;
      }, {}),
    [files]
  );

  return (
    <Modal open={isOpen} onClose={noop} hideCloseButton disableInternalClosing={isPending} className="md:max-w-4xl">
      <ModalHeader>
        <Text className="text-c-card-foreground text-lg font-medium">Upload Files</Text>
      </ModalHeader>

      <ModalBody className="mt-4 mb-4 max-h-[60vh] overflow-auto">
        <div className="border-c-border rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                <TableHeading title="Name" align="left" />
                <TableHeading title="Size" align="right" />
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-c-card-muted h-24 text-center">
                    No files selected for upload.
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => (
                  <UploadFileRow
                    key={file.name}
                    file={file}
                    fileUrl={filesUrls[file.name]}
                    onRemove={onRemove}
                    disabled={disabled || isPending}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button modal as="outline" onClick={onCancel} disabled={disabled || isPending}>
          Cancel
        </Button>
        {onAddMore && (
          <Button modal as="outline" onClick={onAddMore} disabled={disabled || isPending}>
            Add More
          </Button>
        )}
        <Button modal onClick={onUpload} disabled={disabled || isPending || files.length === 0} showLoader={isPending}>
          Upload {files.length > 0 && `(${files.length})`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
